import React from "react";
import { Button, Checkbox, Form, Input, Modal, Select, Spin, message } from "antd";
import { connect } from "react-redux";
import axios from "util/Api";
import AccessControl from "../AccessControl";

class InlineCreateMasterModal extends React.Component {
  state = {
    visible: false,
    loadingOptions: false,
    submitting: false,
    assetTypeOptions: [],
    assetClassificationOptions: [],
    assetSubTypeOptions: [],
    assetMakeOptions: [],
  };

  normalizeItemKey = (value) => ((value || "") + "").toLowerCase().replace(/[^a-z0-9]/g, "");
  isItem = (...aliases) => {
    const current = this.normalizeItemKey(this.props.item);
    return aliases.some((alias) => this.normalizeItemKey(alias) === current);
  };

  showModal = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    this.setState({ visible: true }, async () => {
      if (this.isItem("EquipmentSubType")) {
        await this.loadAssetTypeOptions();
      } else if (this.isItem("AssetType")) {
        await this.loadAssetClassificationOptions();
      } else if (this.isItem("AssetModel")) {
        await this.loadAssetModelOptions();
        const preselectedSubTypeId = this.getIdValue(this.props.initialAssetSubTypeId);
        if (preselectedSubTypeId) {
          this.props.form.setFieldsValue({ assetSubType: preselectedSubTypeId });
        }
      }
    });
  };

  hideModal = () => {
    this.setState({ visible: false, loadingOptions: false, submitting: false });
    this.props.form.resetFields();
  };

  getAssetTypeOptionsFromSchema = (schema = {}) => {
    const options = schema && schema.assetType && schema.assetType.data && schema.assetType.data.data;
    return Array.isArray(options) ? options : [];
  };

  loadAssetTypeOptions = async () => {
    this.setState({ loadingOptions: true });
    try {
      const response = await axios.get(`/common/schema?model=EquipmentSubType`);
      this.setState({ assetTypeOptions: this.getAssetTypeOptionsFromSchema(response.data) });
    } catch (error) {
      message.error("Unable to load Asset Type list");
    } finally {
      this.setState({ loadingOptions: false });
    }
  };

  loadAssetClassificationOptions = async () => {
    this.setState({ loadingOptions: true });
    try {
      const response = await axios.get(`/common/schema?model=AssetType`);
      const schema = response && response.data ? response.data : {};
      const assetClassificationOptions =
        schema && schema.assetClassification && schema.assetClassification.data && schema.assetClassification.data.data;
      this.setState({
        assetClassificationOptions: Array.isArray(assetClassificationOptions) ? assetClassificationOptions : [],
      });
    } catch (error) {
      message.error("Unable to load Asset Category list");
    } finally {
      this.setState({ loadingOptions: false });
    }
  };

  loadAssetModelOptions = async () => {
    this.setState({ loadingOptions: true });
    try {
      const response = await axios.get(`/common/schema?model=AssetModel`);
      const schema = response && response.data ? response.data : {};
      const assetSubTypeOptions = schema && schema.assetSubType && schema.assetSubType.data && schema.assetSubType.data.data;
      const assetMakeOptions = schema && schema.assetMake && schema.assetMake.data && schema.assetMake.data.data;
      this.setState({
        assetSubTypeOptions: Array.isArray(assetSubTypeOptions) ? assetSubTypeOptions : [],
        assetMakeOptions: Array.isArray(assetMakeOptions) ? assetMakeOptions : [],
      });
    } catch (error) {
      message.error("Unable to load Asset Model fields");
    } finally {
      this.setState({ loadingOptions: false });
    }
  };

  getModalTitle = () => {
    const { item = "" } = this.props;
    if (this.isItem("AuditVoucher")) {
      return "Create Audit Voucher";
    }
    if (this.isItem("AssetMake")) {
      return "Create Asset Make";
    }
    if (this.isItem("AssetType")) {
      return "Create Asset Type";
    }
    if (this.isItem("EquipmentSubType")) {
      return "Create Asset Sub Type";
    }
    if (this.isItem("AssetModel")) {
      return "Create Asset Model";
    }
    return `Create ${item || "Item"}`;
  };

  normalizeName = (name) => (typeof name === "string" ? name.trim() : "");
  getIdValue = (value) => {
    if (!value) return "";
    if (typeof value === "string" || typeof value === "number") return `${value}`;
    if (typeof value === "object") {
      return `${value._id || value.id || ""}`;
    }
    return "";
  };

  buildAssetTypeFormData = (values = {}) => {
    const formData = new FormData();
    formData.append("name", this.normalizeName(values.name));
    formData.append("description", values.description ? values.description.trim() : "");
    const assetClassification = this.getIdValue(values.assetClassification);
    if (assetClassification) {
      formData.append("assetClassification", assetClassification);
    }
    formData.append("isBowser", values.isBowser ? "true" : "false");
    return formData;
  };

  buildEquipmentSubTypeFormData = (values = {}) => {
    const formData = new FormData();
    formData.append("name", this.normalizeName(values.name));
    formData.append("code", values.code ? `${values.code}`.trim() : "");
    formData.append("assetType", values.assetType || "");
    formData.append("checklist", JSON.stringify(values.checklist || []));
    formData.append("service", JSON.stringify(values.service || []));
    formData.append("documentData", JSON.stringify([]));
    return formData;
  };

  buildVendorFormData = (values = {}) => {
    const formData = new FormData();
    formData.append("name", this.normalizeName(values.name));
    formData.append("documentData", JSON.stringify([]));
    return formData;
  };

  buildManpowerPayload = (values = {}) => ({
    name: this.normalizeName(values.name),
    designation: values.designation ? values.designation.trim() : "",
    rate: values.rate !== undefined && values.rate !== null && `${values.rate}` !== "" ? Number(values.rate) : undefined,
    minimumWorkHours:
      values.minimumWorkHours !== undefined && values.minimumWorkHours !== null && `${values.minimumWorkHours}` !== ""
        ? Number(values.minimumWorkHours)
        : undefined,
    employeeCode: values.employeeCode ? `${values.employeeCode}`.trim() : "",
  });

  buildSimplePayload = (values = {}) => ({
    name: this.normalizeName(values.name),
    description: values.description ? values.description.trim() : "",
  });

  buildEndpointFromItem = () => {
    const item = ((this.props.item || "") + "").trim();
    if (!item) {
      return "";
    }
    const kebab = item
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/[\s_]+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase();
    return `/${kebab}`;
  };

  buildAssetModelFormData = (values = {}) => {
    const formData = new FormData();
    const normalizedSubTypeId = this.getIdValue(values.assetSubType);
    const normalizedMakeId = this.getIdValue(values.assetMake);
    const normalizedModelNumber = values.modelNumber ? `${values.modelNumber}`.trim() : "";
    const generatedName = this.getGeneratedAssetModelName({
      assetSubType: normalizedSubTypeId,
      assetMake: normalizedMakeId,
      modelNumber: normalizedModelNumber,
    });
    formData.append("name", generatedName);
    formData.append("assetSubType", normalizedSubTypeId);
    formData.append("assetMake", normalizedMakeId);
    if (normalizedModelNumber) {
      formData.append("modelNumber", normalizedModelNumber);
    }
    formData.append("isTagDevice", values.isTagDevice ? "true" : "false");
    formData.append("isFuelSensorDevice", values.isFuelSensorDevice ? "true" : "false");
    formData.append("isRfidTag", values.isRfidTag ? "true" : "false");
    formData.append("isBleTag", values.isBleTag ? "true" : "false");
    formData.append("isCountable", values.isCountable ? "true" : "false");
    formData.append("documentData", JSON.stringify([]));
    return formData;
  };

  submitCreate = async (values = {}) => {
    if (this.isItem("AuditVoucher")) {
      return axios.post(`/audit-voucher`, {
        name: this.normalizeName(values.name),
        description: values.description ? values.description.trim() : "",
      });
    }
    if (this.isItem("AssetMake")) {
      return axios.post(`/asset-make`, {
        name: this.normalizeName(values.name),
        description: values.description ? values.description.trim() : "",
      });
    }
    if (this.isItem("AssetType")) {
      return axios.post(`/asset-type`, this.buildAssetTypeFormData(values));
    }
    if (this.isItem("EquipmentSubType")) {
      return axios.post(`/equipment-sub-type`, this.buildEquipmentSubTypeFormData(values));
    }
    if (this.isItem("AssetModel")) {
      return axios.post(`/asset-model`, this.buildAssetModelFormData(values));
    }
    if (this.isItem("Vendor")) {
      return axios.post(`/vendor`, this.buildVendorFormData(values));
    }
    if (this.isItem("ConditionOfAsset", "PhysicalCondition")) {
      return axios.post(`/condition-of-asset`, this.buildSimplePayload(values));
    }
    if (this.isItem("Manpower", "AssetCustodian")) {
      return axios.post(`/manpower`, this.buildManpowerPayload(values));
    }
    const fallbackEndpoint = this.buildEndpointFromItem();
    if (!fallbackEndpoint) {
      throw new Error("Unsupported inline create type");
    }
    return axios.post(fallbackEndpoint, this.buildSimplePayload(values));
  };

  handleSubmit = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const { form, onCreated } = this.props;
    form.validateFields(async (err, values) => {
      if (err) {
        return;
      }
      this.setState({ submitting: true });
      try {
        if (this.isItem("AssetModel")) {
          const generatedName = this.getGeneratedAssetModelName({
            assetSubType: values.assetSubType,
            assetMake: values.assetMake,
            modelNumber: values.modelNumber,
          });
          if (!generatedName) {
            message.error("Please select Asset Sub Type and Make to generate the Asset Model Name.");
            this.setState({ submitting: false });
            return;
          }
        }
        const response = await this.submitCreate(values);
        const created = response && response.data;
        if (created && created.code === 202) {
          message.error(created.message || "Already exists");
          this.setState({ submitting: false });
          return;
        }
        if (!created || !created._id) {
          message.error("Unable to create record");
          this.setState({ submitting: false });
          return;
        }
        let createdPayload = created;
        if (this.isItem("AssetModel")) {
          const selectedSubTypeId = this.getIdValue(values.assetSubType);
          const selectedMakeId = this.getIdValue(values.assetMake);
          const selectedSubType = (this.state.assetSubTypeOptions || []).find(
            (option) => `${option && option._id}` === selectedSubTypeId
          );
          const selectedMake = (this.state.assetMakeOptions || []).find(
            (option) => `${option && option._id}` === selectedMakeId
          );
          createdPayload = {
            ...created,
            assetSubType: selectedSubType || created.assetSubType,
            assetMake: selectedMake || created.assetMake,
          };
        }
        if (onCreated) {
          onCreated(createdPayload);
        }
        this.hideModal();
      } catch (error) {
        message.error("Unable to create record");
        this.setState({ submitting: false });
      }
    });
  };

  handleAssetTypeCreated = (createdAssetType) => {
    if (!createdAssetType || !createdAssetType._id) {
      return;
    }
    this.setState((prevState) => {
      const alreadyExists = (prevState.assetTypeOptions || []).find((option) => option._id === createdAssetType._id);
      if (alreadyExists) {
        return null;
      }
      return {
        assetTypeOptions: [...(prevState.assetTypeOptions || []), createdAssetType],
      };
    });
    this.props.form.setFieldsValue({ assetType: createdAssetType._id });
  };

  handleAssetSubTypeCreated = (createdAssetSubType) => {
    if (!createdAssetSubType || !createdAssetSubType._id) {
      return;
    }
    this.setState((prevState) => {
      const alreadyExists = (prevState.assetSubTypeOptions || []).find((option) => option._id === createdAssetSubType._id);
      if (alreadyExists) {
        return null;
      }
      return {
        assetSubTypeOptions: [...(prevState.assetSubTypeOptions || []), createdAssetSubType],
      };
    });
    this.props.form.setFieldsValue({ assetSubType: createdAssetSubType._id });
  };

  handleAssetClassificationCreated = (createdAssetClassification) => {
    if (!createdAssetClassification || !createdAssetClassification._id) {
      return;
    }
    this.setState((prevState) => {
      const alreadyExists = (prevState.assetClassificationOptions || []).find(
        (option) => option._id === createdAssetClassification._id
      );
      if (alreadyExists) {
        return null;
      }
      return {
        assetClassificationOptions: [...(prevState.assetClassificationOptions || []), createdAssetClassification],
      };
    });
    this.props.form.setFieldsValue({ assetClassification: createdAssetClassification._id });
  };

  handleAssetMakeCreated = (createdAssetMake) => {
    if (!createdAssetMake || !createdAssetMake._id) {
      return;
    }
    this.setState((prevState) => {
      const alreadyExists = (prevState.assetMakeOptions || []).find((option) => option._id === createdAssetMake._id);
      if (alreadyExists) {
        return null;
      }
      return {
        assetMakeOptions: [...(prevState.assetMakeOptions || []), createdAssetMake],
      };
    });
    this.props.form.setFieldsValue({ assetMake: createdAssetMake._id });
  };

  getOptionNameById = (selectedId, options = []) => {
    const selectedIdValue = this.getIdValue(selectedId);
    if (!selectedIdValue) {
      return "";
    }
    const selected = (options || []).find((option) => `${option && option._id}` === selectedIdValue);
    return selected && selected.name ? selected.name : "";
  };

  composeAssetModelName = (assetMakeName = "", assetSubTypeName = "", modelNumber = "") => {
    const nameParts = [(assetMakeName || "").trim(), (assetSubTypeName || "").trim()];
    const normalizedModelNumber = modelNumber && `${modelNumber}`.trim();
    if (normalizedModelNumber) {
      nameParts.push(normalizedModelNumber);
    }
    return nameParts.filter(Boolean).join(" ");
  };

  getGeneratedAssetModelName = (values = {}) => {
    const { assetSubTypeOptions, assetMakeOptions } = this.state;
    const assetSubTypeName = this.getOptionNameById(values.assetSubType, assetSubTypeOptions);
    const assetMakeName = this.getOptionNameById(values.assetMake, assetMakeOptions);
    return this.composeAssetModelName(assetMakeName, assetSubTypeName, values.modelNumber);
  };

  renderAssetMakeFields = () => {
    const { getFieldDecorator } = this.props.form;
    return (
      <>
        <Form.Item label="Name">
          {getFieldDecorator("name", {
            rules: [{ required: true, message: "Name is required" }],
          })(<Input autoComplete="off" placeholder="Please enter Name" />)}
        </Form.Item>
        <Form.Item label="Description">
          {getFieldDecorator("description")(<Input autoComplete="off" placeholder="Please enter Description" />)}
        </Form.Item>
      </>
    );
  };

  renderSimpleNameFields = ({ nameLabel = "Name", namePlaceholder = "Please enter Name", includeDescription = true } = {}) => {
    const { getFieldDecorator } = this.props.form;
    return (
      <>
        <Form.Item label={nameLabel}>
          {getFieldDecorator("name", {
            rules: [{ required: true, message: `${nameLabel} is required` }],
          })(<Input autoComplete="off" placeholder={namePlaceholder} />)}
        </Form.Item>
        {includeDescription ? (
          <Form.Item label="Description">
            {getFieldDecorator("description")(<Input autoComplete="off" placeholder="Please enter Description" />)}
          </Form.Item>
        ) : null}
      </>
    );
  };

  renderManpowerFields = () => {
    const { getFieldDecorator } = this.props.form;
    return (
      <>
        <Form.Item label="Name">
          {getFieldDecorator("name", {
            rules: [{ required: true, message: "Name is required" }],
          })(<Input autoComplete="off" placeholder="Please enter Name" />)}
        </Form.Item>
        <Form.Item label="Designation">
          {getFieldDecorator("designation", {
            rules: [{ required: true, message: "Designation is required" }],
          })(<Input autoComplete="off" placeholder="Please enter Designation" />)}
        </Form.Item>
        <Form.Item label="Rate">
          {getFieldDecorator("rate", {
            rules: [
              { required: true, message: "Rate is required" },
              { pattern: /^\d+(\.\d+)?$/, message: "Rate must be a number" },
            ],
          })(<Input autoComplete="off" placeholder="Please enter Rate" />)}
        </Form.Item>
        <Form.Item label="Minimum Work Hours">
          {getFieldDecorator("minimumWorkHours", {
            rules: [
              { required: true, message: "Minimum Work Hours is required" },
              { pattern: /^\d+(\.\d+)?$/, message: "Minimum Work Hours must be a number" },
            ],
          })(<Input autoComplete="off" placeholder="Please enter Minimum Work Hours" />)}
        </Form.Item>
        <Form.Item label="Employee Code">
          {getFieldDecorator("employeeCode")(<Input autoComplete="off" placeholder="Please enter Employee Code (optional)" />)}
        </Form.Item>
      </>
    );
  };

  renderAuditVoucherFields = () => {
    const { getFieldDecorator } = this.props.form;
    return (
      <>
        <Form.Item label="Name">
          {getFieldDecorator("name", {
            rules: [{ required: true, message: "Name is required" }],
          })(<Input autoComplete="off" placeholder="Please enter Name" />)}
        </Form.Item>
        <Form.Item label="Description">
          {getFieldDecorator("description")(<Input autoComplete="off" placeholder="Please enter Description" />)}
        </Form.Item>
      </>
    );
  };

  renderAssetTypeFields = () => {
    const { getFieldDecorator } = this.props.form;
    const { assetClassificationOptions, loadingOptions } = this.state;
    return (
      <>
        <Form.Item label="Name">
          {getFieldDecorator("name", {
            rules: [{ required: true, message: "Name is required" }],
          })(<Input autoComplete="off" placeholder="Please enter Name" />)}
        </Form.Item>
        <Form.Item label="Asset Category">
          <div style={{ display: "flex", alignItems: "center" }}>
            {getFieldDecorator("assetClassification")(
              <Select
                showSearch
                allowClear
                style={{ width: "80%" }}
                placeholder="Please select Asset Category"
                loading={loadingOptions}
                filterOption={(input, option) =>
                  (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                }
              >
                {(assetClassificationOptions || []).map((option) => (
                  <Select.Option key={option._id} value={option._id}>
                    {option.name}
                  </Select.Option>
                ))}
              </Select>
            )}
            <InlineCreateMasterModalForm
              item="AssetClassification"
              onCreated={this.handleAssetClassificationCreated}
              triggerStyle={{
                margin: 0,
                marginTop: 0,
                marginLeft: "10px",
                width: "calc(100% - 10px)",
              }}
            />
          </div>
        </Form.Item>
        <Form.Item label="Description">
          {getFieldDecorator("description")(<Input autoComplete="off" placeholder="Please enter Description" />)}
        </Form.Item>
      </>
    );
  };

  renderEquipmentSubTypeFields = () => {
    const { getFieldDecorator } = this.props.form;
    const { assetTypeOptions, loadingOptions } = this.state;
    return (
      <>
        <Form.Item label="Name">
          {getFieldDecorator("name", {
            rules: [{ required: true, message: "Name is required" }],
          })(<Input autoComplete="off" placeholder="Please enter Name" />)}
        </Form.Item>
        <Form.Item label="Code">
          {getFieldDecorator("code", {
            rules: [{ required: true, message: "Code is required" }],
          })(<Input autoComplete="off" placeholder="Please enter Code" />)}
        </Form.Item>
        <Form.Item label="Asset Type">
          <div style={{ display: "flex", alignItems: "center" }}>
            {getFieldDecorator("assetType", {
              rules: [{ required: true, message: "Asset Type is required" }],
            })(
              <Select
                showSearch
                allowClear
                style={{ width: "80%" }}
                placeholder="Please select Asset Type"
                loading={loadingOptions}
                filterOption={(input, option) =>
                  (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                }
              >
                {(assetTypeOptions || []).map((option) => (
                  <Select.Option key={option._id} value={option._id}>
                    {option.name}
                  </Select.Option>
                ))}
              </Select>
            )}
            <InlineCreateMasterModalForm
              item="AssetType"
              onCreated={this.handleAssetTypeCreated}
              triggerStyle={{
                margin: 0,
                marginTop: 0,
                marginLeft: "10px",
                width: "calc(100% - 10px)",
              }}
            />
          </div>
        </Form.Item>
      </>
    );
  };

  renderAssetModelFields = () => {
    const { getFieldDecorator, getFieldValue, setFieldsValue } = this.props.form;
    const { assetSubTypeOptions, assetMakeOptions, loadingOptions } = this.state;
    const generatedName = this.getGeneratedAssetModelName({
      assetSubType: getFieldValue("assetSubType"),
      assetMake: getFieldValue("assetMake"),
      modelNumber: getFieldValue("modelNumber"),
    });
    return (
      <>
        <Form.Item label="Asset Sub Type">
          <div style={{ display: "flex", alignItems: "center" }}>
            {getFieldDecorator("assetSubType", {
              rules: [{ required: true, message: "Asset Sub Type is required" }],
            })(
              <Select
                showSearch
                allowClear
                style={{ width: "80%" }}
                placeholder="Please select Asset Sub Type"
                loading={loadingOptions}
                onChange={() => setFieldsValue({ assetMake: undefined })}
                filterOption={(input, option) =>
                  (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                }
              >
                {(assetSubTypeOptions || []).map((option) => (
                  <Select.Option key={option._id} value={option._id}>
                    {option.name}
                  </Select.Option>
                ))}
              </Select>
            )}
            <InlineCreateMasterModalForm
              item="EquipmentSubType"
              onCreated={this.handleAssetSubTypeCreated}
              triggerStyle={{
                margin: 0,
                marginTop: 0,
                marginLeft: "10px",
                width: "calc(100% - 10px)",
              }}
            />
          </div>
        </Form.Item>
        <Form.Item label="Make">
          <div style={{ display: "flex", alignItems: "center" }}>
            {getFieldDecorator("assetMake", {
              rules: [{ required: true, message: "Asset Make is required" }],
            })(
              <Select
                showSearch
                allowClear
                style={{ width: "80%" }}
                placeholder="Please select Asset Make"
                loading={loadingOptions}
                disabled={!getFieldValue("assetSubType")}
                filterOption={(input, option) =>
                  (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                }
              >
                {(assetMakeOptions || []).map((option) => (
                  <Select.Option key={option._id} value={option._id}>
                    {option.name}
                  </Select.Option>
                ))}
              </Select>
            )}
            <InlineCreateMasterModalForm
              item="AssetMake"
              onCreated={this.handleAssetMakeCreated}
              triggerStyle={{
                margin: 0,
                marginTop: 0,
                marginLeft: "10px",
                width: "calc(100% - 10px)",
              }}
            />
          </div>
        </Form.Item>
        <Form.Item label="Model Number">
          {getFieldDecorator("modelNumber")(<Input autoComplete="off" placeholder="Please enter Model Number (optional)" />)}
        </Form.Item>
        <Form.Item label="Generated Asset Model Name">
          <Input value={generatedName || "Will be generated after selecting Asset Sub Type and Make"} disabled />
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("isCountable", {
            valuePropName: "checked",
            initialValue: false,
          })(<Checkbox>Is Countable?</Checkbox>)}
          <div style={{ color: "#8c8c8c", marginLeft: 24 }}>
            Countable assets can be created in bulk with a quantity
          </div>
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("isTagDevice", {
            valuePropName: "checked",
            initialValue: false,
          })(<Checkbox>Asset is a GPRS Device?</Checkbox>)}
          <div style={{ color: "#8c8c8c", marginLeft: 24 }}>
            Mark true if the asset will be used as a GPRS device
          </div>
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("isFuelSensorDevice", {
            valuePropName: "checked",
            initialValue: false,
          })(<Checkbox>Is Fuel Sensor?</Checkbox>)}
          <div style={{ color: "#8c8c8c", marginLeft: 24 }}>
            Mark true if the asset is a IOT Fuel Sensor device
          </div>
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("isRfidTag", {
            valuePropName: "checked",
            initialValue: false,
          })(<Checkbox>Is RFID Tag?</Checkbox>)}
          <div style={{ color: "#8c8c8c", marginLeft: 24 }}>
            Mark true if the asset is an RFID tag device
          </div>
        </Form.Item>
        <Form.Item>
          {getFieldDecorator("isBleTag", {
            valuePropName: "checked",
            initialValue: false,
          })(<Checkbox>Is BLE Tag?</Checkbox>)}
          <div style={{ color: "#8c8c8c", marginLeft: 24 }}>
            Mark true if the asset is a BLE tag device
          </div>
        </Form.Item>
      </>
    );
  };

  renderFormFields = () => {
    if (this.isItem("AuditVoucher")) {
      return this.renderAuditVoucherFields();
    }
    if (this.isItem("AssetMake")) {
      return this.renderAssetMakeFields();
    }
    if (this.isItem("AssetType")) {
      return this.renderAssetTypeFields();
    }
    if (this.isItem("EquipmentSubType")) {
      return this.renderEquipmentSubTypeFields();
    }
    if (this.isItem("AssetModel")) {
      return this.renderAssetModelFields();
    }
    if (this.isItem("Vendor")) {
      return this.renderSimpleNameFields({ includeDescription: false });
    }
    if (this.isItem("ConditionOfAsset", "PhysicalCondition")) {
      return this.renderSimpleNameFields({ nameLabel: "Condition", namePlaceholder: "Please enter Condition", includeDescription: false });
    }
    if (this.isItem("Manpower", "AssetCustodian")) {
      return this.renderManpowerFields();
    }
    return this.renderSimpleNameFields();
  };

  renderModal = () => {
    const { visible, submitting, loadingOptions } = this.state;
    return (
      <Modal title={this.getModalTitle()} visible={visible} onCancel={this.hideModal} footer={null} destroyOnClose>
        <Spin spinning={submitting || loadingOptions}>
          <Form layout="vertical" onSubmit={this.handleSubmit}>
            {this.renderFormFields()}
            <Form.Item>
              <Button type="primary" onClick={this.handleSubmit} loading={submitting}>
                CREATE
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    );
  };

  renderTriggerButton = () => {
    const { triggerStyle, item, authUser, disabled } = this.props;
    return (
      <AccessControl
        resource={item}
        permissionNeeded={"c"}
        roles={authUser ? authUser.roles : []}
        render={() => (
          <Button type="dashed" onClick={this.showModal} style={triggerStyle} disabled={disabled}>
            NEW
          </Button>
        )}
      />
    );
  };

  render() {
    return (
      <span style={{ width: "20%", display: "inline-block" }}>
        {this.renderModal()}
        {this.renderTriggerButton()}
      </span>
    );
  }
}

const WrappedInlineModal = Form.create({ name: "inline_create_master_form" })(InlineCreateMasterModal);

const mapStateToProps = ({ auth }) => ({
  authUser: auth.authUser,
});

export const InlineCreateMasterModalForm = connect(mapStateToProps)(WrappedInlineModal);

export default InlineCreateMasterModalForm;
